import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Button,
  ButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Tooltip,
  Paper,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  useTheme,
  Snackbar,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Language as WebIcon,
  Shop as PlayIcon,
  Campaign as CampaignIcon,
  ContentCopy as CopyIcon,
  CheckCircle as CheckIcon,
  InstallMobile as InstallIcon,
  Analytics as AnalyticsIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
} from 'recharts';
import { attributionApi } from '../../services/attributionApi';
import type {
  AttributionReport,
  RecentInstallItem,
  BreakdownItem,
} from '../../models/attribution';

const CHANNEL_COLORS: Record<string, string> = {
  play_organic: '#10b981', // Emerald
  ai11_web: '#8b5cf6', // Violet
  play_campaign: '#3b82f6', // Blue
  unavailable: '#f59e0b', // Amber
  unattributed: '#94a3b8', // Slate
  other: '#ec4899', // Pink
};

const CHANNEL_LABELS: Record<string, string> = {
  play_organic: 'Play Store Organic',
  ai11_web: 'Website (ai11_web)',
  play_campaign: 'Campaign / Ads',
  unavailable: 'Referrer Unavailable',
  unattributed: 'Unattributed',
  other: 'Other Referrer',
};

const PIE_PALETTE = ['#8b5cf6', '#10b981', '#3b82f6', '#f59e0b', '#ec4899', '#06b6d4', '#64748b'];

export const InstallAttributionDashboard: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [days, setDays] = useState<number>(30);
  const [platform, setPlatform] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<AttributionReport | null>(null);

  const [activeTab, setActiveTab] = useState<number>(0);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedInstall, setSelectedInstall] = useState<RecentInstallItem | null>(null);
  const [copyNotice, setCopyNotice] = useState<string | null>(null);

  const fetchData = useCallback(async (isManualRefresh = false) => {
    if (isManualRefresh) setRefreshing(true);
    else setLoading(true);
    setError(null);
    try {
      const data = await attributionApi.getStats({
        days,
        platform: platform || undefined,
      });
      setReport(data);
    } catch (err: any) {
      console.error('Failed to load attribution stats:', err);
      setError(err?.response?.data?.detail || err?.message || 'Failed to load install attribution stats');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [days, platform]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCopy = (text: string, label: string) => {
    if (navigator?.clipboard) {
      navigator.clipboard.writeText(text);
      setCopyNotice(`Copied ${label} to clipboard!`);
    }
  };

  const summary = report?.summary;

  const channelPieData = useMemo(() => {
    if (!report?.channels?.length) return [];
    return report.channels.map((ch) => ({
      name: CHANNEL_LABELS[ch.name] || ch.name,
      value: ch.count,
      rawName: ch.name,
      percentage: ch.percentage,
    }));
  }, [report]);

  const mediumBarData = useMemo(() => {
    if (!report?.mediums?.length) return [];
    return report.mediums.slice(0, 8).map((m) => ({
      medium: m.name,
      installs: m.count,
      percentage: m.percentage,
    }));
  }, [report]);

  const filteredRecentInstalls = useMemo(() => {
    if (!report?.recent_installs) return [];
    if (!searchQuery.trim()) return report.recent_installs;
    const q = searchQuery.toLowerCase();
    return report.recent_installs.filter(
      (item) =>
        item.id.toLowerCase().includes(q) ||
        (item.medium && item.medium.toLowerCase().includes(q)) ||
        (item.source && item.source.toLowerCase().includes(q)) ||
        (item.campaign && item.campaign.toLowerCase().includes(q)) ||
        (item.page && item.page.toLowerCase().includes(q)) ||
        (item.referrer && item.referrer.toLowerCase().includes(q))
    );
  }, [report, searchQuery]);

  const cardBg = isDark ? 'rgba(30, 32, 45, 0.7)' : 'rgba(255, 255, 255, 0.9)';
  const borderCol = isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)';

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1600, margin: '0 auto' }}>
      {/* Top Header Bar */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <AnalyticsIcon sx={{ fontSize: 32, color: theme.palette.primary.main }} />
            <Typography variant="h4" fontWeight={700} sx={{ letterSpacing: -0.5 }}>
              Install Attribution
            </Typography>
            <Chip
              label="App 1.6.0+"
              size="small"
              color="primary"
              variant="outlined"
              sx={{ fontWeight: 600, fontSize: '0.75rem' }}
            />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Real-time tracking of app install origins: Play Store referrer, website placements, and promotional campaigns.
          </Typography>
        </Box>

        {/* Filter Toolbar */}
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
          {/* Days selector */}
          <ButtonGroup size="small" variant="outlined">
            {[
              { label: '7D', val: 7 },
              { label: '14D', val: 14 },
              { label: '30D', val: 30 },
              { label: '90D', val: 90 },
              { label: 'All', val: 0 },
            ].map((btn) => (
              <Button
                key={btn.val}
                variant={days === btn.val ? 'contained' : 'outlined'}
                onClick={() => setDays(btn.val)}
                sx={{ px: 1.8, fontWeight: 600 }}
              >
                {btn.label}
              </Button>
            ))}
          </ButtonGroup>

          {/* Platform selector */}
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel id="platform-select-label">Platform</InputLabel>
            <Select
              labelId="platform-select-label"
              value={platform}
              label="Platform"
              onChange={(e) => setPlatform(e.target.value)}
            >
              <MenuItem value="">All Platforms</MenuItem>
              <MenuItem value="android">Android</MenuItem>
              <MenuItem value="ios">iOS</MenuItem>
              <MenuItem value="web">Web</MenuItem>
            </Select>
          </FormControl>

          {/* Refresh Button */}
          <Tooltip title="Refresh Stats">
            <span>
              <IconButton
                onClick={() => fetchData(true)}
                disabled={loading || refreshing}
                sx={{
                  bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                  border: `1px solid ${borderCol}`,
                  borderRadius: 2,
                }}
              >
                <RefreshIcon
                  sx={{
                    animation: refreshing ? 'spin 1s linear infinite' : 'none',
                    '@keyframes spin': {
                      '0%': { transform: 'rotate(0deg)' },
                      '100%': { transform: 'rotate(360deg)' },
                    },
                  }}
                />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading && !refreshing ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 12, gap: 2 }}>
          <CircularProgress size={48} />
          <Typography color="text.secondary">Loading install attribution metrics...</Typography>
        </Box>
      ) : (
        <>
          {/* Hero KPI Cards */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
                lg: 'repeat(5, 1fr)',
              },
              gap: 2,
              mb: 3,
            }}
          >
            {/* Total Installs */}
            <Card
              sx={{
                background: isDark
                  ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(30, 32, 45, 0.8) 100%)'
                  : 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, #ffffff 100%)',
                border: `1px solid ${isDark ? 'rgba(99, 102, 241, 0.3)' : 'rgba(99, 102, 241, 0.2)'}`,
                borderRadius: 3,
                boxShadow: isDark ? '0 4px 20px rgba(0,0,0,0.3)' : '0 4px 20px rgba(99,102,241,0.08)',
              }}
            >
              <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                    Total Installs
                  </Typography>
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 2,
                      bgcolor: 'rgba(99, 102, 241, 0.15)',
                      color: '#6366f1',
                      display: 'flex',
                    }}
                  >
                    <InstallIcon fontSize="small" />
                  </Box>
                </Box>
                <Typography variant="h3" fontWeight={700} sx={{ my: 1 }}>
                  {summary?.total_installs?.toLocaleString() ?? 0}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Registered clients in period
                </Typography>
              </CardContent>
            </Card>

            {/* Attributed Installs */}
            <Card
              sx={{
                background: isDark
                  ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(30, 32, 45, 0.8) 100%)'
                  : 'linear-gradient(135deg, rgba(16, 185, 129, 0.08) 0%, #ffffff 100%)',
                border: `1px solid ${isDark ? 'rgba(16, 185, 129, 0.3)' : 'rgba(16, 185, 129, 0.2)'}`,
                borderRadius: 3,
                boxShadow: isDark ? '0 4px 20px rgba(0,0,0,0.3)' : '0 4px 20px rgba(16,185,129,0.08)',
              }}
            >
              <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                    Attributed Installs
                  </Typography>
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 2,
                      bgcolor: 'rgba(16, 185, 129, 0.15)',
                      color: '#10b981',
                      display: 'flex',
                    }}
                  >
                    <CheckIcon fontSize="small" />
                  </Box>
                </Box>
                <Typography variant="h3" fontWeight={700} sx={{ my: 1, color: '#10b981' }}>
                  {summary?.attributed_installs?.toLocaleString() ?? 0}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label={`${summary?.attribution_rate ?? 0}% Rate`}
                    size="small"
                    sx={{
                      bgcolor: 'rgba(16, 185, 129, 0.15)',
                      color: '#10b981',
                      fontWeight: 700,
                      height: 20,
                      fontSize: '0.7rem',
                    }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {summary?.unattributed_installs ?? 0} unattributed
                  </Typography>
                </Box>
              </CardContent>
            </Card>

            {/* Website Driven (ai11_web) */}
            <Card
              sx={{
                background: isDark
                  ? 'linear-gradient(135deg, rgba(139, 92, 246, 0.15) 0%, rgba(30, 32, 45, 0.8) 100%)'
                  : 'linear-gradient(135deg, rgba(139, 92, 246, 0.08) 0%, #ffffff 100%)',
                border: `1px solid ${isDark ? 'rgba(139, 92, 246, 0.3)' : 'rgba(139, 92, 246, 0.2)'}`,
                borderRadius: 3,
                boxShadow: isDark ? '0 4px 20px rgba(0,0,0,0.3)' : '0 4px 20px rgba(139,92,246,0.08)',
              }}
            >
              <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                    Website (ai11.in)
                  </Typography>
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 2,
                      bgcolor: 'rgba(139, 92, 246, 0.15)',
                      color: '#8b5cf6',
                      display: 'flex',
                    }}
                  >
                    <WebIcon fontSize="small" />
                  </Box>
                </Box>
                <Typography variant="h3" fontWeight={700} sx={{ my: 1, color: '#8b5cf6' }}>
                  {summary?.website_installs?.toLocaleString() ?? 0}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Via banners, hero, blog & AMP
                </Typography>
              </CardContent>
            </Card>

            {/* Play Organic */}
            <Card
              sx={{
                background: isDark
                  ? 'linear-gradient(135deg, rgba(6, 182, 212, 0.15) 0%, rgba(30, 32, 45, 0.8) 100%)'
                  : 'linear-gradient(135deg, rgba(6, 182, 212, 0.08) 0%, #ffffff 100%)',
                border: `1px solid ${isDark ? 'rgba(6, 182, 212, 0.3)' : 'rgba(6, 182, 212, 0.2)'}`,
                borderRadius: 3,
                boxShadow: isDark ? '0 4px 20px rgba(0,0,0,0.3)' : '0 4px 20px rgba(6,182,212,0.08)',
              }}
            >
              <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                    Play Store Organic
                  </Typography>
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 2,
                      bgcolor: 'rgba(6, 182, 212, 0.15)',
                      color: '#06b6d4',
                      display: 'flex',
                    }}
                  >
                    <PlayIcon fontSize="small" />
                  </Box>
                </Box>
                <Typography variant="h3" fontWeight={700} sx={{ my: 1, color: '#06b6d4' }}>
                  {summary?.organic_play_installs?.toLocaleString() ?? 0}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Direct search on Google Play
                </Typography>
              </CardContent>
            </Card>

            {/* Campaigns & Other */}
            <Card
              sx={{
                background: isDark
                  ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(30, 32, 45, 0.8) 100%)'
                  : 'linear-gradient(135deg, rgba(59, 130, 246, 0.08) 0%, #ffffff 100%)',
                border: `1px solid ${isDark ? 'rgba(59, 130, 246, 0.3)' : 'rgba(59, 130, 246, 0.2)'}`,
                borderRadius: 3,
                boxShadow: isDark ? '0 4px 20px rgba(0,0,0,0.3)' : '0 4px 20px rgba(59,130,246,0.08)',
              }}
            >
              <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                    Campaigns & Ads
                  </Typography>
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 2,
                      bgcolor: 'rgba(59, 130, 246, 0.15)',
                      color: '#3b82f6',
                      display: 'flex',
                    }}
                  >
                    <CampaignIcon fontSize="small" />
                  </Box>
                </Box>
                <Typography variant="h3" fontWeight={700} sx={{ my: 1, color: '#3b82f6' }}>
                  {summary?.campaign_installs?.toLocaleString() ?? 0}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  UTM campaigns & Google Ads
                </Typography>
              </CardContent>
            </Card>
          </Box>

          {/* Charts Row */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' },
              gap: 3,
              mb: 3,
            }}
          >
            {/* Timeline Area Chart */}
            <Card sx={{ bgcolor: cardBg, border: `1px solid ${borderCol}`, borderRadius: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box>
                    <Typography variant="h6" fontWeight={700}>
                      Acquisition Timeline
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Daily install trends and attribution breakdown
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ width: '100%', height: 320 }}>
                  {report?.timeline && report.timeline.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={report.timeline} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                        <defs>
                          <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#6366f1" stopOpacity={0.4} />
                            <stop offset="95%" stopColor="#6366f1" stopOpacity={0.0} />
                          </linearGradient>
                          <linearGradient id="colorWeb" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.6} />
                            <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.0} />
                          </linearGradient>
                          <linearGradient id="colorOrganic" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#10b981" stopOpacity={0.6} />
                            <stop offset="95%" stopColor="#10b981" stopOpacity={0.0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke={isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'} />
                        <XAxis
                          dataKey="date"
                          stroke={isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)'}
                          tick={{ fontSize: 11 }}
                          tickFormatter={(v) => v.slice(5)}
                        />
                        <YAxis stroke={isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)'} tick={{ fontSize: 11 }} />
                        <RechartsTooltip
                          contentStyle={{
                            backgroundColor: isDark ? '#1d1f2b' : '#ffffff',
                            borderColor: borderCol,
                            borderRadius: 8,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                            fontSize: 12,
                          }}
                        />
                        <Legend wrapperStyle={{ fontSize: 12, paddingTop: 10 }} />
                        <Area
                          type="monotone"
                          dataKey="total"
                          name="Total Installs"
                          stroke="#6366f1"
                          strokeWidth={2}
                          fillOpacity={1}
                          fill="url(#colorTotal)"
                        />
                        <Area
                          type="monotone"
                          dataKey="web"
                          name="Website Installs"
                          stroke="#8b5cf6"
                          strokeWidth={2}
                          fillOpacity={1}
                          fill="url(#colorWeb)"
                        />
                        <Area
                          type="monotone"
                          dataKey="organic"
                          name="Organic Play"
                          stroke="#10b981"
                          strokeWidth={2}
                          fillOpacity={1}
                          fill="url(#colorOrganic)"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                      <Typography color="text.secondary">No timeline data available for this range</Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>

            {/* Channels Donut Chart */}
            <Card sx={{ bgcolor: cardBg, border: `1px solid ${borderCol}`, borderRadius: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight={700}>
                  Channel Distribution
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Share by install channel
                </Typography>

                <Box sx={{ width: '100%', height: 320, mt: 1 }}>
                  {channelPieData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={channelPieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={65}
                          outerRadius={95}
                          paddingAngle={3}
                          dataKey="value"
                        >
                          {channelPieData.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={CHANNEL_COLORS[entry.rawName] || PIE_PALETTE[index % PIE_PALETTE.length]}
                            />
                          ))}
                        </Pie>
                        <RechartsTooltip
                          formatter={(value: any, name: any, item: any) => [
                            `${value} (${item.payload.percentage}%)`,
                            name,
                          ]}
                          contentStyle={{
                            backgroundColor: isDark ? '#1d1f2b' : '#ffffff',
                            borderColor: borderCol,
                            borderRadius: 8,
                            fontSize: 12,
                          }}
                        />
                        <Legend
                          verticalAlign="bottom"
                          layout="horizontal"
                          wrapperStyle={{ fontSize: 11, paddingTop: 10 }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                      <Typography color="text.secondary">No channel attribution data yet</Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Box>

          {/* Website Placements Bar Chart (if data present) */}
          {mediumBarData.length > 0 && (
            <Card sx={{ bgcolor: cardBg, border: `1px solid ${borderCol}`, borderRadius: 3, mb: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight={700}>
                  Website Placements (UTM Medium)
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Conversions driven by website components and banners
                </Typography>
                <Box sx={{ width: '100%', height: 260, mt: 2 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={mediumBarData} margin={{ top: 10, right: 10, left: -20, bottom: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'} />
                      <XAxis
                        dataKey="medium"
                        stroke={isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)'}
                        tick={{ fontSize: 11 }}
                      />
                      <YAxis stroke={isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)'} tick={{ fontSize: 11 }} />
                      <RechartsTooltip
                        contentStyle={{
                          backgroundColor: isDark ? '#1d1f2b' : '#ffffff',
                          borderColor: borderCol,
                          borderRadius: 8,
                          fontSize: 12,
                        }}
                      />
                      <Bar dataKey="installs" name="Installs" fill="#8b5cf6" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Tabs Section for Breakdown Tables & Live Feed */}
          <Paper sx={{ bgcolor: cardBg, border: `1px solid ${borderCol}`, borderRadius: 3, overflow: 'hidden' }}>
            <Box
              sx={{
                borderBottom: `1px solid ${borderCol}`,
                px: 3,
                pt: 1,
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Tabs
                value={activeTab}
                onChange={(_, val) => setActiveTab(val)}
                textColor="primary"
                indicatorColor="primary"
              >
                <Tab label="Recent Installs (Live)" sx={{ fontWeight: 600, textTransform: 'none' }} />
                <Tab label="Placements (Mediums)" sx={{ fontWeight: 600, textTransform: 'none' }} />
                <Tab label="Source Pages" sx={{ fontWeight: 600, textTransform: 'none' }} />
                <Tab label="Campaigns & Refs" sx={{ fontWeight: 600, textTransform: 'none' }} />
              </Tabs>

              {activeTab === 0 && (
                <TextField
                  size="small"
                  placeholder="Filter recent installs..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  sx={{ width: { xs: '100%', sm: 260 }, my: 1 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            </Box>

            {/* TAB 0: Recent Installs Feed */}
            {activeTab === 0 && (
              <Box sx={{ p: 2 }}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Time</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Client ID</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Channel</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Medium / Placement</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Source Page</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Campaign / Ref</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Platform / Version</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Details</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredRecentInstalls.length > 0 ? (
                        filteredRecentInstalls.map((row) => {
                          const channelColor = CHANNEL_COLORS[row.channel] || '#64748b';
                          return (
                            <TableRow key={row.id} hover>
                              <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.8rem', color: 'text.secondary' }}>
                                {row.created_at ? new Date(row.created_at).toLocaleString() : '—'}
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                                    {row.id.slice(0, 8)}...
                                  </Typography>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleCopy(row.id, 'Client ID')}
                                    sx={{ p: 0.2 }}
                                  >
                                    <CopyIcon sx={{ fontSize: 14 }} />
                                  </IconButton>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={row.channel}
                                  size="small"
                                  sx={{
                                    bgcolor: `${channelColor}22`,
                                    color: channelColor,
                                    fontWeight: 700,
                                    fontSize: '0.72rem',
                                    border: `1px solid ${channelColor}44`,
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                {row.medium ? (
                                  <Chip
                                    label={row.medium}
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontWeight: 600, fontSize: '0.75rem' }}
                                  />
                                ) : (
                                  <Typography variant="body2" color="text.secondary">—</Typography>
                                )}
                              </TableCell>
                              <TableCell sx={{ maxWidth: 220 }}>
                                {row.page ? (
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontFamily: 'monospace',
                                      fontSize: '0.78rem',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}
                                    title={row.page}
                                  >
                                    {row.page}
                                  </Typography>
                                ) : (
                                  <Typography variant="body2" color="text.secondary">—</Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                {row.campaign || row.ref ? (
                                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                    {row.campaign && (
                                      <Chip label={row.campaign} size="small" color="primary" sx={{ height: 20, fontSize: '0.7rem' }} />
                                    )}
                                    {row.ref && (
                                      <Chip label={row.ref} size="small" color="secondary" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                                    )}
                                  </Box>
                                ) : (
                                  <Typography variant="body2" color="text.secondary">—</Typography>
                                )}
                              </TableCell>
                              <TableCell sx={{ fontSize: '0.8rem' }}>
                                {row.platform || 'android'} · v{row.version_name || '—'}
                              </TableCell>
                              <TableCell align="right">
                                <Button
                                  size="small"
                                  variant="text"
                                  onClick={() => setSelectedInstall(row)}
                                  sx={{ textTransform: 'none', py: 0.2 }}
                                >
                                  View
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      ) : (
                        <TableRow>
                          <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                              <InfoIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
                              <Typography variant="body1" fontWeight={600}>
                                No recent attributed installs found
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 450 }}>
                                Once users install app version 1.6.0+ via website links or Google Play, their attribution logs will stream here.
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {/* TAB 1: Mediums / Placements */}
            {activeTab === 1 && (
              <Box sx={{ p: 3 }}>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Placement / Medium</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Install Count</TableCell>
                        <TableCell sx={{ fontWeight: 700, width: '40%' }}>Share of Attributed</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {report?.mediums && report.mediums.length > 0 ? (
                        report.mediums.map((item: BreakdownItem) => (
                          <TableRow key={item.name} hover>
                            <TableCell>
                              <Chip label={item.name} sx={{ fontWeight: 600 }} />
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '1rem' }}>
                              {item.count.toLocaleString()}
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Box sx={{ width: '100%', mr: 1 }}>
                                  <LinearProgress
                                    variant="determinate"
                                    value={item.percentage}
                                    sx={{ height: 8, borderRadius: 4, bgcolor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)' }}
                                  />
                                </Box>
                                <Typography variant="body2" fontWeight={600} sx={{ minWidth: 45 }}>
                                  {item.percentage}%
                                </Typography>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={3} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                            No placement / medium data available yet
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {/* TAB 2: Source Pages */}
            {activeTab === 2 && (
              <Box sx={{ p: 3 }}>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Referring Page Path</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Installs</TableCell>
                        <TableCell sx={{ fontWeight: 700, width: '40%' }}>Share</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {report?.pages && report.pages.length > 0 ? (
                        report.pages.map((item: BreakdownItem) => (
                          <TableRow key={item.name} hover>
                            <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                              {item.name}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>
                              {item.count.toLocaleString()}
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Box sx={{ width: '100%', mr: 1 }}>
                                  <LinearProgress
                                    variant="determinate"
                                    value={item.percentage}
                                    sx={{ height: 8, borderRadius: 4, bgcolor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)' }}
                                    color="secondary"
                                  />
                                </Box>
                                <Typography variant="body2" fontWeight={600} sx={{ minWidth: 45 }}>
                                  {item.percentage}%
                                </Typography>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={3} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                            No referring page paths recorded yet
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {/* TAB 3: Campaigns & Refs */}
            {activeTab === 3 && (
              <Box sx={{ p: 3 }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                  {/* Campaigns */}
                  <Card sx={{ bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)', border: `1px solid ${borderCol}` }}>
                    <CardContent>
                      <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
                        Top Campaigns
                      </Typography>
                      {report?.campaigns && report.campaigns.length > 0 ? (
                        report.campaigns.map((c) => (
                          <Box key={c.name} sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: `1px solid ${borderCol}` }}>
                            <Typography variant="body2" fontWeight={600}>{c.name}</Typography>
                            <Typography variant="body2" color="primary" fontWeight={700}>{c.count} ({c.percentage}%)</Typography>
                          </Box>
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">No campaigns recorded yet</Typography>
                      )}
                    </CardContent>
                  </Card>

                  {/* Ref Tags */}
                  <Card sx={{ bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)', border: `1px solid ${borderCol}` }}>
                    <CardContent>
                      <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
                        Referral / Partner Tags (ref)
                      </Typography>
                      {report?.refs && report.refs.length > 0 ? (
                        report.refs.map((r) => (
                          <Box key={r.name} sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: `1px solid ${borderCol}` }}>
                            <Typography variant="body2" fontWeight={600}>{r.name}</Typography>
                            <Typography variant="body2" color="secondary" fontWeight={700}>{r.count} ({r.percentage}%)</Typography>
                          </Box>
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">No ref tags recorded yet</Typography>
                      )}
                    </CardContent>
                  </Card>
                </Box>
              </Box>
            )}
          </Paper>
        </>
      )}

      {/* Details Dialog */}
      <Dialog
        open={Boolean(selectedInstall)}
        onClose={() => setSelectedInstall(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          Install Attribution Details
        </DialogTitle>
        <DialogContent dividers>
          {selectedInstall && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  CLIENT ID
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {selectedInstall.id}
                  </Typography>
                  <IconButton size="small" onClick={() => handleCopy(selectedInstall.id, 'Client ID')}>
                    <CopyIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    CHANNEL
                  </Typography>
                  <Typography variant="body2" fontWeight={600} sx={{ color: CHANNEL_COLORS[selectedInstall.channel] || 'inherit' }}>
                    {selectedInstall.channel}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    MEDIUM (PLACEMENT)
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {selectedInstall.medium || '—'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    SOURCE
                  </Typography>
                  <Typography variant="body2">
                    {selectedInstall.source || '—'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    CAMPAIGN
                  </Typography>
                  <Typography variant="body2">
                    {selectedInstall.campaign || '—'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    SOURCE PAGE
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {selectedInstall.page || '—'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    REF TAG
                  </Typography>
                  <Typography variant="body2">
                    {selectedInstall.ref || '—'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    PLATFORM & VERSION
                  </Typography>
                  <Typography variant="body2">
                    {selectedInstall.platform} v{selectedInstall.version_name}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    INSTALLED AT
                  </Typography>
                  <Typography variant="body2">
                    {selectedInstall.created_at ? new Date(selectedInstall.created_at).toLocaleString() : '—'}
                  </Typography>
                </Box>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  RAW PLAY STORE REFERRER STRING
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    mt: 0.5,
                    bgcolor: isDark ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.03)',
                    fontFamily: 'monospace',
                    fontSize: '0.8rem',
                    wordBreak: 'break-all',
                    borderRadius: 2,
                    position: 'relative',
                  }}
                >
                  {selectedInstall.referrer || 'None (organic/unrecorded)'}
                  {selectedInstall.referrer && (
                    <IconButton
                      size="small"
                      onClick={() => handleCopy(selectedInstall.referrer, 'Referrer String')}
                      sx={{ position: 'absolute', top: 4, right: 4 }}
                    >
                      <CopyIcon fontSize="small" />
                    </IconButton>
                  )}
                </Paper>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedInstall(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Copy Toast */}
      <Snackbar
        open={Boolean(copyNotice)}
        autoHideDuration={2500}
        onClose={() => setCopyNotice(null)}
        message={copyNotice}
      />
    </Box>
  );
};

export default InstallAttributionDashboard;
